import React, { useEffect, useState } from 'react';
import {
	Box,
	Typography,
	IconButton,
	Button,
	MenuItem,
	Select,
	Dialog,
	DialogContent,
	CircularProgress,
	TextField,
	useMediaQuery,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { Close } from '@mui/icons-material';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { useTheme } from '@mui/material/styles';
import Transition from './Transition';

export interface ConfirmDialogProps {
	open?: boolean;
	action?: 'approve' | 'reject' | null;
	onClose?: () => void;
	onConfirm?: (reason?: string, percentage?: number) => void;
	loader?: boolean;
	request?: any;
	vendorApprove?: any;
}

export const MyModal: React.FC<ConfirmDialogProps> = ({
	open = false,
	action = null,
	onClose,
	onConfirm,
	loader,
	request,
	vendorApprove,
}) => {
	const [rejectReason, setRejectReason] = useState<string>('');
	const [productPercentage, setProductPercentage] = useState<number | ''>('');
	const isApprove = action === 'approve';
	console.log(vendorApprove, 'vend');
	useEffect(() => {
		setProductPercentage(vendorApprove?.vendorPercentage || 0);
	}, [vendorApprove]);

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const handleConfirm = () => {
		if (isApprove) {
			// If request exists, no percentage needed
			if (request) {
				onConfirm?.(undefined, undefined);
			} else {
				onConfirm?.(undefined, Number(productPercentage));
			}
		} else {
			onConfirm?.(rejectReason);
		}

		setRejectReason('');
		setProductPercentage('');
	};

	const handleClose = () => {
		setRejectReason('');
		setProductPercentage('');
		onClose?.();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			TransitionComponent={Transition}
			maxWidth="sm"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: 3,
					overflow: 'visible',
				},
			}}
		>
			<DialogContent sx={{ p: 4, overflow: 'visible' }}>
				{/* Close Button */}
				<IconButton
					onClick={handleClose}
					size="small"
					sx={{
						position: 'absolute',
						top: 16,
						right: 16,
						bgcolor: 'grey.100',
						'&:hover': {
							bgcolor: 'grey.200',
						},
					}}
					aria-label="close"
				>
					<Close fontSize="small" />
				</IconButton>

				{/* Animated Icon (HIDE in mobile) */}
				{!isMobile && (
					<Box
						sx={{
							position: 'relative',
							width: 100,
							height: 100,
							m: '32px auto 24px',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<AnimatePresence mode="wait">
							<motion.div
								key={isApprove ? 'approve' : 'reject'}
								initial={{ scale: 0, rotate: -180 }}
								animate={{ scale: 1, rotate: 0 }}
								exit={{ scale: 0, rotate: 90 }}
								transition={{ type: 'spring', stiffness: 300, damping: 20 }}
							>
								<Box
									sx={{
										width: 80,
										height: 80,
										borderRadius: '50%',
										background: isApprove
											? 'linear-gradient(135deg, #4ade80 0%, #16a34a 100%)'
											: 'linear-gradient(135deg, #f87171 0%, #dc2626 100%)',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										boxShadow: '0 6px 12px rgba(0,0,0,0.1)',
										'&::after': {
											content: '""',
											position: 'absolute',
											width: '100%',
											height: '100%',
											borderRadius: '50%',
											border: isApprove
												? '4px solid #bbf7d0'
												: '4px solid #fecaca',
											opacity: 0.5,
											transform: 'scale(1.1)',
										},
									}}
								>
									<QuestionMarkIcon sx={{ fontSize: 48, color: 'white' }} />
								</Box>
							</motion.div>
						</AnimatePresence>
					</Box>
				)}

				{/* Title */}
				<Typography
					variant="h4"
					sx={{
						color: isApprove ? '#166534' : '#b91c1c',
						fontWeight: 600,
						fontSize: '1.5rem',
						textAlign: 'center',
						mb: 2,
					}}
				>
					{isApprove ? 'Approve Vendor Product?' : 'Reject Vendor Product?'}
				</Typography>

				{/* Description */}
				<Typography
					variant="body1"
					sx={{
						color: 'text.secondary',
						mb: 1,
						textAlign: 'center',
						fontSize: '12px',
					}}
				>
					{isApprove
						? request
							? 'Do you want to approve this product?'
							: 'Please provide a percentage before approving this product.'
						: 'Please provide a reason for rejecting this product. This will help the vendor understand what needs to be improved.'}
				</Typography>

				{/* Reject dropdown */}
				{!isApprove && (
					<Box sx={{ mb: 4 }}>
						<Typography sx={{ mb: 1, fontWeight: 500, textAlign: 'left' }}>
							Reason
						</Typography>
						<Select
							fullWidth
							value={rejectReason}
							onChange={(e) => setRejectReason(e.target.value)}
							displayEmpty
							renderValue={(selected) => {
								if (!selected) {
									return (
										<Typography color="text.disabled">
											Select a reason
										</Typography>
									);
								}
								return selected;
							}}
							sx={{
								borderRadius: 2,
								textAlign: 'left',
							}}
						>
							<MenuItem value="Incorrect Information">Incorrect Information</MenuItem>
							<MenuItem value="Inappropriate Content">Inappropriate Content</MenuItem>
							<MenuItem value="Pricing Issue">Pricing Issue</MenuItem>
							<MenuItem value="Other">Other</MenuItem>
						</Select>
					</Box>
				)}

				{/* Approve input (only if no request) */}
				{isApprove && !request && (
					<Box sx={{ mb: 4 }}>
						<Typography sx={{ mb: 1, fontWeight: 500, textAlign: 'left' }}>
							Product Percentage (%)
						</Typography>
						<TextField
							fullWidth
							type="number"
							value={productPercentage}
							onChange={(e) =>
								setProductPercentage(e.target.value ? Number(e.target.value) : '')
							}
							placeholder="Enter percentage"
							inputProps={{ min: 0, max: 100 }}
						/>
					</Box>
				)}

				{/* Action Buttons */}
				<Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
					<Button
						variant="outlined"
						onClick={handleClose}
						sx={{
							borderRadius: 2,
							px: 4,
							py: 1,
							fontWeight: 600,
							borderWidth: 2,
						}}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={handleConfirm}
						disabled={
							loader ||
							(isApprove && !request && productPercentage === '') ||
							(!isApprove && !rejectReason)
						}
						sx={{
							background: isApprove ? 'green' : 'red',
							borderRadius: 2,
							px: 4,
							py: 1,
							fontWeight: 600,
							boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
							'&:disabled': { opacity: 0.6 },
						}}
					>
						{loader ? <CircularProgress size={24} color="inherit" /> : 'Confirm'}
					</Button>
				</Box>
			</DialogContent>
		</Dialog>
	);
};
